import React, { useMemo, createRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Avatar, Divider } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";

import Text from "components/common/Text";
import Button from "components/common/Button";
import PhoneInput from "components/common/PhoneInput";
import PageLoader from "components/common/PageLoader";
import { usePermissions } from "components/Hook";
import { AlertType } from "components/common/Snackbar";
import ImageCropperModal from "components/common/ImageCropperModal";
import SearchableAutoComplete from "components/common/AutoComplete";
import { isEmptyObject, isValidResponse } from "components/Screens/CommonUtils";
import ManagedEmployeeList from "components/Screens/SettingV2/AddUser/ManagedEmployeeList/index";

import { DropdownData } from "store/actions/DropdownData";
import { GetGroups } from "store/actions/GetGroups";
import { GetRoles } from "store/actions/Rbac";
import { showSnackbar } from "store/actions/Utility";
import { AddUser, ResetAddUser } from "store/actions/Settings/AddUser";
import { GetUserDetails, ResetGetUserDetails } from "store/actions/Settings/GetUserDetails";
import { assignRole } from "services/rbac";
import { assignMembers } from "services/groups";
import { hasPermission } from "hooks/usePermission";
import { baseUrl } from "util/APIBaseUrl";

import CustomerUserSchema from "validations/CustomerUserSchema";
import { EditOutlined, ArrowBackOutlined } from "@mui/icons-material";

import "./add-user.scss";
import { CardHeader } from "components/common/Card";

function AddCustomerUser(props) {
  const { navigatedFor, paramId, setAddorEditUser, editableUser, managersList, customerId } = props;

  const photoInput = createRef();
  const dispatch = useDispatch();
  const { loggedInUserType, dropdownData } = usePermissions();
  const { customerViewerRole } = useFlags();

  const [showImageCropperModal, setShowImageCropperModal] = useState(false);
  const [croppedUserImage, setCroppedUserImage] = useState();
  const [dropdownData_, setDropdownData_] = useState({});
  const [imageSrc, setImageSrc] = useState();
  const [imageFile, setImageFile] = useState();
  const [permDropdownOption, setPermDropdownOption] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFileUploadError, setIsFileUploadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Role id chosen at submit time; assigned via the dedicated endpoint after the user is created/updated.
  const [pendingRoleId, setPendingRoleId] = useState(null);
  const [pendingGroupId, setPendingGroupId] = useState(null);

  const getUserDetails = useSelector((state) => state.GetUserDetails.getUserDetails);
  const addUser = useSelector((state) => state.AddUser.addUser || {});
  const { groups } = useSelector((state) => state.GetGroups || {});
  const { roles } = useSelector((state) => state.Rbac || {});

  // Whether the current caller can assign roles; gates the Role selector.
  const canAssignRole = useMemo(() => hasPermission("roles.assign"), []);
  // Whether the current caller can move a user between groups; gates the Group selector.
  const canAssignMember = useMemo(() => hasPermission("groups.assign_member"), []);

  // This component (AddCustomerUser) only ever creates/edits CUSTOMER users — it is used by the
  // customer details Users tab and the customer-admin settings screen, never to manage edge users.
  // So the role User_Type and assignRole principalType are always customer. Deriving these from
  // loggedInUserType is wrong: an Edge admin editing a customer user would otherwise see Edge roles
  // and assign the role with the wrong principal type.
  const { roleUserType, principalType } = useMemo(
    () => ({ roleUserType: "customer", principalType: "customerUser" }),
    []
  );

  const groupOptions = useMemo(
    () =>
      (groups || []).map((g) => ({
        value: g.Group_ID,
        label: g.Is_Main ? `${g.Name} (Main)` : g.Name,
      })),
    [groups]
  );

  const roleOptions = useMemo(
    () =>
      (roles || [])
        .filter((r) => r.User_Type === roleUserType)
        .map((r) => ({ value: r.Role_ID, label: r.Name })),
    [roles, roleUserType]
  );

  const actionMode = useMemo(
    () => ({
      editUser: {
        customer: {
          logoRequest: `customerUser/${paramId}/logo?id=${getUserDetails?.payload?.pictureVersion}`,
        },
        edge: {
          logoRequest: `customerUser/${paramId}/logo?id=${getUserDetails?.payload?.pictureVersion}`,
        },
      },

      addUser: {
        customer: {
          logoRequest: `customerUser/${addUser?.payload?.data?.customerUserId}/logo`,
        },
        edge: {
          logoRequest: `customerUser/${addUser?.payload?.data?.customerUserId}/logo`,
        },
      },
    }),
    [getUserDetails, addUser, paramId]
  );

  const methods = useForm({
    resolver: yupResolver(CustomerUserSchema),
    mode: "onChange",
    defaultValues: {
      firstName: editableUser?.firstName || "",
      lastName: editableUser?.lastName || "",
      email: editableUser?.email || "",
      phoneNumber: editableUser?.phoneNumber || "",
      permissions: (editableUser?.permissions && editableUser?.permissions[0]) || "",
      group: editableUser?.groupId || "",
      role: editableUser?.roleId || "",
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    trigger,
  } = methods;

  const onSubmit = async (data) => {
    if (isValid && !isSubmitting) {
      setIsSubmitting(true);
      setIsLoading(true);
      setPendingRoleId(canAssignRole && data?.role ? data.role : null);
      setPendingGroupId(data?.group || null);
      try {
        // The selected group differs from the user's current group only when the form's value
        // changed. Group membership is never persisted through the generic customer-user update —
        // it must go through the guarded members endpoint (assignMembers). For edge admins / non-edge
        // contexts alike the principalType is customerUser (this component only edits customer users).
        const groupChanged =
          navigatedFor === "editUser" && canAssignMember && data?.group && data.group !== editableUser?.groupId;

        if (data?.permissions) {
          const userActionMode = {
            editUser: {
              customer: `v2/customerUser/${paramId}`,
              edge: `v2/customerUser/${paramId}`,
              method: "PATCH",
            },

            addUser: {
              customer: "customerUser/",
              edge: "customerUser/",
              method: "POST",
            },
          };

          await dispatch(
            AddUser({
              request: userActionMode[navigatedFor][loggedInUserType],
              userData: data,
              customerId,
              method: userActionMode[navigatedFor]?.method,
            })
          );
          // The role/group are persisted by their dedicated endpoints once AddUser succeeds
          // (see the addUser effect below), so nothing more to do here for the create/update path.
        } else if (groupChanged) {
          // Group-only edit (no profile/permission change). Without this branch the group change was
          // silently dropped: AddUser was never dispatched, so the success effect that assigns the
          // group never ran — the form showed "updated" while the group stayed put. Route it through
          // the guarded endpoint directly and surface backend errors instead of a false success.
          const ok = await assignSelectedGroup(data.group, paramId);
          if (ok) {
            dispatch(
              showSnackbar({
                type: AlertType?.success,
                message: "User updated successfully",
                dispatch,
              })
            );
            setAddorEditUser({ open: false, mode: null });
          }
          setIsLoading(false);
          setIsSubmitting(false);
          setPendingGroupId(null);
        } else {
          setIsLoading(false);
          setIsSubmitting(false);
        }
      } catch (error) {
        dispatch(showSnackbar({ type: AlertType.error, message: error?.message, dispatch }));
        setIsLoading(false);
        setIsSubmitting(false);
      }
    }
  };

  const handleSaveClick = async () => {
    if (!isSubmitting) {
      const result = await trigger();
      if (result) {
        handleSubmit(onSubmit)();
      }
    }
  };

  const uploadProfilePic = () => {
    photoInput.current.click();
  };

  function readFile(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.addEventListener(
        "load",
        () => {
          resolve(reader.result);
        },
        false
      );
      reader.readAsDataURL(file);
    });
  }

  const handleImageChange = async (e) => {
    e.preventDefault();

    const file = e.target.files[0];
    e.target.value = "";

    if (file.size / 1024 / 1024 > 2) setIsFileUploadError(true);
    else setIsFileUploadError(false);

    const imageDataUrl = await readFile(file);
    setImageSrc(imageDataUrl);

    setShowImageCropperModal(true);
  };

  const postLogo = async (actions) => {
    const response = await fetch(`${baseUrl}/${actions.request}`, {
      method: "POST",
      headers: {},
      body: actions.formData,
    });
    if (!response.ok) {
      return;
    }
    await response.json();
  };

  const handleImageSave = async (param) => {
    const formData = new FormData();
    const imageDataUrl = await readFile(param);
    setCroppedUserImage(imageDataUrl);

    formData?.append("file", param);

    setImageFile(formData);
    setShowImageCropperModal(false);

    if (navigatedFor === "editUser") {
      postLogo({
        request: actionMode[navigatedFor][loggedInUserType].logoRequest,
        formData,
      });
    }
  };

  // Assign the selected role via the dedicated endpoint after the user is created/updated.
  // The create/update paths do NOT accept roleId, so this is a separate POST /v4/roles/:roleId/assign.
  const assignSelectedRole = async () => {
    if (!pendingRoleId) return;
    const principalId =
      navigatedFor === "editUser" ? paramId : addUser?.payload?.data?.customerUserId;
    if (!principalId) {
      setPendingRoleId(null);
      return;
    }
    try {
      const res = await assignRole(pendingRoleId, principalType, principalId);
      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType?.success, message: "Role assigned successfully", dispatch }));
      } else {
        dispatch(
          showSnackbar({
            type: AlertType?.error,
            message: res?.payload?.message || "Failed to assign role",
            dispatch,
          })
        );
      }
    } catch (error) {
      dispatch(showSnackbar({ type: AlertType?.error, message: "Failed to assign role", dispatch }));
    } finally {
      setPendingRoleId(null);
    }
  };

  // Group membership is changed via the dedicated, scope-guarded POST /groups/:groupId/members
  // endpoint (the user create/update paths must not silently move a user between groups, and the
  // members endpoint enforces the owner-must-stay-in-Main / out-of-scope / self-reassign guards).
  // The selected group differing from the user's current group is the only trigger; on failure the
  // backend's message is surfaced so we never falsely report success. Returns true on success
  // (or when there is nothing to change), false when the assignment was attempted and rejected.
  const assignSelectedGroup = async (groupId, principalId) => {
    if (!canAssignMember || !groupId || !principalId) return true;
    if (navigatedFor === "editUser" && groupId === editableUser?.groupId) return true;
    try {
      const res = await assignMembers(groupId, [{ principalType, principalId }]);
      if (res && res.ok) return true;
      dispatch(
        showSnackbar({
          type: AlertType?.error,
          message: res?.payload?.message || "Failed to update group",
          dispatch,
        })
      );
      return false;
    } catch (error) {
      dispatch(showSnackbar({ type: AlertType?.error, message: "Failed to update group", dispatch }));
      return false;
    }
  };

  useEffect(() => {
    if (isValidResponse(addUser)) {
      (async () => {
        if (navigatedFor === "addUser") {
          postLogo({
            request: actionMode[navigatedFor][loggedInUserType].logoRequest,
            formData: imageFile,
          });
        }

        // Persist role + group via their dedicated endpoints before closing, so the
        // refreshed list reflects the change. The group goes through the guarded members
        // endpoint; if the backend rejects it (owner-must-stay-in-Main / out-of-scope /
        // self-reassign), surface that error rather than the generic success message.
        await assignSelectedRole();
        const groupPrincipalId =
          navigatedFor === "editUser" ? paramId : addUser?.payload?.data?.customerUserId;
        const groupOk = await assignSelectedGroup(pendingGroupId, groupPrincipalId);

        if (groupOk) {
          dispatch(
            showSnackbar({
              type: AlertType?.success,
              message: navigatedFor === "editUser" ? "User updated successfully" : "User added successfully",
              dispatch,
            })
          );
        }
        setPendingGroupId(null);
        setAddorEditUser({
          open: false,
          mode: null,
        });
      })();
    } else if (Object.keys(addUser).length && addUser?.ok == false) {
      dispatch(
        showSnackbar({
          type: AlertType?.error,
          message: addUser?.payload?.error[0]?.description,
          dispatch,
        })
      );

      setIsSubmitting(false);
      setIsLoading(false);
    }
    return () => {
      dispatch(ResetAddUser());
      dispatch(ResetGetUserDetails({}));
    };
  }, [addUser]);

  useEffect(() => {
    setIsLoading(true);
    let loggedInUser = {};
    let permissionsOption = [];

    loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));

    dispatch(DropdownData());
    // Use the customer being edited (passed as a prop) and only fall back to the logged-in
    // user's own customer. An Edge admin has no customerId of their own, so relying on
    // loggedInUser.customerId here would call /v4/groups without a customer.
    dispatch(GetGroups({ customerId: customerId || loggedInUser?.customerId }));
    if (canAssignRole && !(roles || []).length) {
      dispatch(GetRoles());
    }

    if (isEmptyObject(loggedInUser)) {
      permissionsOption = [
        {
          title: "Customer Admin",
          description: "Customer admin can add new users, edit company and payment details ",
          value: "CustomerAdmin",
        },
        {
          title: "Customer Employee",
          description: "Limited access user",
          value: "CustomerEmployee",
        },
      ];
      if (customerViewerRole) {
        permissionsOption.push({
          title: "Customer Viewer",
          description: `Can view workforce and time-off but cannot access financial or ${
            loggedInUser?.type === "customer" ? "billing" : "payroll"
          } data`,
          value: "CustomerViewer",
        });
      }

      setPermDropdownOption(
        permissionsOption.map((perm) => ({
          label: perm.title,
          value: perm.value,
          description: perm.description,
          render: (option) => {
            return (
              <CardHeader
                key="title"
                subheader={option.description}
                isSmallHeading
                title={option.label}
                sx={{ padding: 0 }}
              />
            );
          },
        }))
      );
    }

    if (isEmptyObject(loggedInUser) && navigatedFor === "editUser" && paramId) {
      const userDetailsRequest = {
        customer: `customerUser/${paramId}`,
        edge: `customerUser/${paramId}`,
      };

      dispatch(
        GetUserDetails({
          request: userDetailsRequest[loggedInUserType],
        })
      );
    }

    if (!Object.keys(dropdownData_).length) {
      const countries = [];
      const edgePermissions = [];
      const customerPermissions = [];
      let Permissions = [];

      if (isValidResponse(dropdownData)) {
        dropdownData?.payload?.country?.map((data) => {
          return countries.push({
            key: data.countryCode2,
            value: data.countryCode,
          });
        });
        dropdownData?.payload?.edgePermission?.map((perm) => {
          return edgePermissions.push({
            key: perm.description,
            value: perm.permissionName,
            valueDescription: perm.permissionName == "EdgeAdmin" ? "Admin" : "Employee",
          });
        });

        dropdownData?.payload?.customerPermission?.map((perm) => {
          return customerPermissions.push({
            key: perm.description,
            value: perm.permissionName,
            valueDescription: perm.permissionName == "CustomerAdmin" ? "Admin" : "Employee",
          });
        });

        if (loggedInUser?.type == "customer") {
          Permissions = customerPermissions;
        } else if (loggedInUser?.type == "edge") {
          Permissions = edgePermissions;
        }

        setDropdownData_({
          ...dropdownData_,
          countries,
          Permissions,
        });
      }
    }
    setIsLoading(false);
  }, []);

  return (
    <>
      {isLoading && <PageLoader />}
      <ImageCropperModal
        open={showImageCropperModal}
        onClose={() => setShowImageCropperModal(false)}
        imageSrc={imageSrc}
        onSave={handleImageSave}
        isFileUploadError={isFileUploadError}
        errorMessage="The file you've selected is too large. Please upload an image file that is 2MB or smaller."
      />
      <div>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <ArrowBackOutlined
                  className="cursor-pointer"
                  onClick={() => setAddorEditUser({ open: false, mode: null })}
                />
                <p className="heading-h3-semibold">{navigatedFor == "editUser" ? "Edit a User" : "Add a User"}</p>
              </div>
              <div className="flex items-center gap-05">
                <Button
                  onClick={() => setAddorEditUser({ open: false, mode: null })}
                  label="Cancel"
                  size="large"
                  variant="text"
                />

                <Button
                  isLoading={isLoading}
                  variant="contained"
                  color={!isValid && "disabled"}
                  size="large"
                  label={navigatedFor === "editUser" ? "Save" : "Add User"}
                  type="submit"
                  onClick={handleSaveClick}
                  disabled={!isValid}
                />
              </div>
            </div>
            <div className={`add-user-content ${loggedInUserType === "edge" && "w-70p"}`}>
              <div className="user-img-container">
                <div className="relative cursor-pointer" onClick={uploadProfilePic}>
                  <Avatar
                    variant="rounded"
                    className="user-img-avt"
                    imgProps={{ crossOrigin: "anonymous" }}
                    src={
                      croppedUserImage ??
                      `${baseUrl}/customerUser/${getUserDetails?.payload?.userId}/logo?id=${getUserDetails?.payload?.pictureVersion}`
                    }
                  />

                  <div className="edit-upload-icon">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      ref={photoInput}
                      style={{ display: "none" }}
                    />
                    <EditOutlined className="color-icon-primary" />
                  </div>
                </div>
              </div>
              <div className="w-70p">
                <p className="heading-h4-semibold">User Information</p>
                <div className="space-y-2 w-full mt-15">
                  <div className="flex gap-1">
                    <Text
                      name="firstName"
                      label="First Name"
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                    <Text
                      name="lastName"
                      label="Last Name"
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  </div>

                  <div className="flex gap-1">
                    <Text
                      name="email"
                      label="Email Address"
                      error={!!errors.email}
                      disabled={navigatedFor === "editUser"}
                      helperText={errors.email?.message}
                    />
                    <PhoneInput
                      name="phoneNumber"
                      label="Phone number"
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                    />
                  </div>

                  <div className="flex gap-1">
                    <SearchableAutoComplete
                      name="permissions"
                      label="Permissions"
                      options={permDropdownOption}
                      placeholder="Select"
                      error={!!errors.state}
                      helperText={errors.state?.message}
                    />
                    {canAssignMember && (
                      <SearchableAutoComplete
                        name="group"
                        label="Group"
                        options={groupOptions}
                        placeholder="Select group"
                        isOptional
                        error={!!errors.group}
                        helperText={errors.group?.message}
                      />
                    )}
                  </div>

                  {canAssignRole && (
                    <div className="flex gap-1">
                      <SearchableAutoComplete
                        name="role"
                        label="Role"
                        options={roleOptions}
                        placeholder="Select role"
                        isOptional
                        error={!!errors.role}
                        helperText={errors.role?.message}
                      />
                    </div>
                  )}
                </div>
                {editableUser?.userId && (
                  <>
                    <div className="mt-125 mb-125">
                      <Divider />
                    </div>
                    <div className="mb-3 pb-025">
                      <ManagedEmployeeList customerUserId={editableUser.userId} managersList={managersList} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
}

export default AddCustomerUser;
